import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@clover/web-lib-consumer-ui';
import { MerchantCurrency } from 'components/MerchantCurrency';
import { PLACE_ORDER_BUTTON_TEST_ID } from 'routes/Checkout/const';
import { useCheckoutValidity } from '../context/CheckoutValidity';
import { useMerchant } from 'hooks/context/merchant';
import { useSubmitOrder } from '../context/SubmitOrder/SubmitOrder';
import { useOrderTotals } from '../context/useOrderTotals';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useDeliveryPlacementConditions } from 'hooks/useDeliveryPlacementConditions';
import { useGetUnavailableItems } from 'hooks/useGetUnavailableItems';

const PlaceOrderButton: React.FC<React.ComponentProps<typeof Button>> = ({ children, disabled, ...props }) => {
  const { isOrderValid, isPaymentFormValid, isOnlyIpg } = useCheckoutValidity();
  const { isOrderProcessing, isReCaptchaReady, hasUnrecoverableError } = useSubmitOrder();
  const { isLoading: isMerchantLoading } = useMerchant();
  const { isAcceptingOrders } = useMerchantState();
  const { disableDeliverySubmit } = useDeliveryPlacementConditions();
  const { hasUnavailableItems } = useGetUnavailableItems();
  const { totalWithTip } = useOrderTotals();

  return (
    <Button
      data-testid={PLACE_ORDER_BUTTON_TEST_ID.root}
      disabled={
        disabled ||
        (!isAcceptingOrders && !isOrderValid) ||
        isMerchantLoading ||
        isOrderProcessing ||
        !isReCaptchaReady ||
        !isOrderValid ||
        hasUnrecoverableError ||
        disableDeliverySubmit ||
        hasUnavailableItems ||
        (!isOnlyIpg && (!isPaymentFormValid && totalWithTip > 0))
      }
      {...props}
    >
      {children}
    </Button>
  );
};

type PlaceOrderButtonProps = Omit<React.ComponentProps<typeof Button>, 'children'> & {
  children?: never;
};
export const TabletPlaceOrderButton: React.FC<PlaceOrderButtonProps> = ({
  children, disabled, ...props
}) => {
  const { t } = useTranslation();
  const { totalWithSurcharge } = useOrderTotals();
  const { showRunningOrderState } = useMerchantState();
  const buttonContent = showRunningOrderState ? t('scan2order.completePayment'): t('checkoutPage.submit');

  return (
    <PlaceOrderButton {...props}>
      <span>{buttonContent}</span>
      &nbsp;•&nbsp;
      <MerchantCurrency amount={totalWithSurcharge} disablePlaceholder />
    </PlaceOrderButton>
  );
};

export const MobilePlaceOrderButton: React.FC<PlaceOrderButtonProps> = ({
  children, disabled, ...props
}) => {
  const { t } = useTranslation();
  const { isOrderValid } = useCheckoutValidity();
  const { totalWithSurcharge } = useOrderTotals();
  const { showRunningOrderState } = useMerchantState();
  const buttonContent = showRunningOrderState ? t('scan2order.completePayment'): t('checkoutPage.submit');

  if (!isOrderValid) return null;

  return (
    <PlaceOrderButton {...props}>
      <span>{buttonContent}</span>
      <MerchantCurrency amount={totalWithSurcharge} disablePlaceholder />
    </PlaceOrderButton>
  );
};
